import {
  forwardRef,
  useMemo,
  useRef,
  type ReactElement,
  type ReactNode,
  Children,
  Fragment,
  cloneElement,
} from 'react';
import { useIntl } from 'react-intl';
import { isElement } from 'react-is';

import { DotMenuIcon } from '@dynatrace/strato-icons';

import {
  ActionButton,
  isAppHeaderActionButton,
  type AppHeaderActionButtonOwnProps,
} from './ActionButton.js';
import { ActionItemGroup } from './ActionItemGroup.js';
import { Button } from '../../buttons/button/Button.js';
import { useContainerSize } from '../../core/hooks/useContainerSize.js';
import { DataTestId } from '../../core/types/data-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { Menu } from '../../navigation/menu/Menu.js';
import { Tooltip } from '../../overlays/tooltip/Tooltip.js';
import type { TooltipProps } from '../../overlays/tooltip/tooltip.types.js';
import { Divider } from '../divider/Divider.js';
import { Flex } from '../flex/Flex.js';

function getSlots(children: ReactNode) {
  const actions: ReactElement<AppHeaderActionButtonOwnProps | TooltipProps>[] =
    [];
  const actionGroups: {
    [groupId: string]: ReactElement<
      AppHeaderActionButtonOwnProps | TooltipProps
    >[];
  } = {};
  const custom: ReactNode[] = [];

  let groupCounter = 0;

  for (const child of Children.toArray(children)) {
    if (!isElement(child)) {
      continue;
    }

    const childWithKey = cloneElement(child, {
      key: child.key ?? crypto.randomUUID(),
    });

    if (isSlot(child, ActionItemGroup)) {
      groupCounter++;
      actionGroups[groupCounter] = [];
      const actionGroup = actionGroups[groupCounter];

      const validChildren = Children.toArray(child.props.children).filter(
        (
          validChild,
        ): validChild is ReactElement<
          AppHeaderActionButtonOwnProps | TooltipProps
        > =>
          (isSlot(validChild, Tooltip) &&
            isSlot(validChild.props.children, ActionButton)) ||
          isSlot(validChild, ActionButton),
      );

      validChildren.forEach((validChild) => actionGroup.push(validChild));
    } else if (isAppHeaderActionButton(child)) {
      actions.push(childWithKey);
    } else if (
      isSlot(child, Tooltip) &&
      isSlot(child.props.children, ActionButton)
    ) {
      actions.push(child);
    } else {
      custom.push(childWithKey);
    }
  }
  return { actions, actionGroups, custom };
}

function useCollapse() {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const containerSize = useContainerSize(containerRef);
  const contentSize = useContainerSize(contentRef);

  return {
    isOverflowing: contentSize.width > containerSize.width,
    containerRef,
    contentRef,
  };
}

/**
 * @public
 */
export interface AppHeaderActionItemsProps
  extends WithChildren,
    StylingProps,
    DataTestId {}

/**
 * The action items section of the AppHeader, which collapses into an overlay on small screen sizes.
 * @public
 */
export const ActionItems = forwardRef<
  HTMLDivElement,
  AppHeaderActionItemsProps
>(({ children, className, style, 'data-testid': dataTestId, ...rest }, ref) => {
  const slots = useMemo(() => getSlots(children), [children]);
  const collapse = useCollapse();
  const intl = useIntl();

  const hasActions = slots.actions.length > 0;
  const hasGroups = Object.entries(slots.actionGroups).length > 0;

  const isCollapsedIntoMenu =
    collapse.isOverflowing && (hasActions || hasGroups);

  return (
    <>
      <Flex
        ref={ref}
        alignItems="center"
        justifyContent="flex-end"
        flexGrow={1}
        flexShrink={0}
        flexBasis={hasActions || hasGroups ? 0 : 'auto'}
        minWidth={0}
        style={style}
        className={className}
        data-testid={dataTestId}
      >
        <Flex
          {...rest}
          ref={collapse.containerRef}
          justifyContent="flex-end"
          alignItems="center"
          flexGrow={1}
          minWidth={0}
          hidden={collapse.isOverflowing}
          style={{
            visibility: collapse.isOverflowing ? 'hidden' : undefined,
            flexBasis: collapse.isOverflowing ? 0 : undefined,
          }}
        >
          <Flex
            gap={4}
            style={{ minWidth: 'max-content' }}
            ref={collapse.contentRef}
            alignItems="center"
          >
            {/**
             * `slots.custom` is intentionally rendered conditionally even if the parent is hidden.
             * This ensures correct flex sizing calculations to determine whether the actions can fit or need to collapse.
             */}
            {collapse.isOverflowing ? null : slots.custom}
            {slots.actions.map((action) => {
              if (isSlot(action, ActionButton)) {
                const { prefixIcon, children, showLabel, ...rest } =
                  action.props;

                return (
                  <Button {...rest} key={action.key} variant="emphasized">
                    {prefixIcon ? (
                      <Button.Prefix>{prefixIcon}</Button.Prefix>
                    ) : null}
                    {children}
                  </Button>
                );
              } else if (
                isSlot(action, Tooltip) &&
                isSlot(action.props.children, ActionButton)
              ) {
                return action;
              }

              return null;
            })}

            {hasActions && hasGroups ? (
              <Divider
                orientation="vertical"
                flexItem={false}
                style={{ height: 20 }}
              />
            ) : null}

            {Object.entries(slots.actionGroups).map(
              ([key, actions], actionGroupIndex) => (
                <Fragment key={key}>
                  {actionGroupIndex !== 0 && (
                    <Divider
                      orientation="vertical"
                      flexItem={false}
                      style={{ height: 20 }}
                    />
                  )}
                  {actions.map((action) => {
                    if (isSlot(action, ActionButton)) {
                      const { prefixIcon, children, showLabel, ...rest } =
                        action.props;

                      return (
                        <Button {...rest} key={action.key} variant="emphasized">
                          {prefixIcon ? (
                            <Button.Prefix>{prefixIcon}</Button.Prefix>
                          ) : null}
                          {children}
                        </Button>
                      );
                    } else if (
                      isSlot(action, Tooltip) &&
                      isSlot(action.props.children, ActionButton)
                    ) {
                      return action;
                    }

                    return null;
                  })}
                </Fragment>
              ),
            )}
          </Flex>
        </Flex>
      </Flex>
      {collapse.isOverflowing ? (
        <Flex gap={4} alignItems="center">
          {slots.custom}
        </Flex>
      ) : null}
      {isCollapsedIntoMenu ? (
        <Menu>
          <Menu.Trigger>
            <Button
              variant="default"
              aria-label={intl.formatMessage({
                id: 'L/D6X7Ee5PYWzl3i',
                defaultMessage: 'Show more actions',
              })}
            >
              <Button.Prefix>
                <DotMenuIcon />
              </Button.Prefix>
            </Button>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Group>
              {slots.actions.map((action) => {
                if (isSlot(action, ActionButton)) {
                  const { prefixIcon, children, showLabel, onClick, ...rest } =
                    action.props;

                  return (
                    <Menu.Item
                      {...rest}
                      key={action.key}
                      onSelect={
                        (onClick ? onClick : () => undefined) as () => void
                      } // onClick of ActionButton type expects a MouseEvent at the moment, should maybe be widened
                    >
                      {prefixIcon ? (
                        <Menu.Prefix>{prefixIcon}</Menu.Prefix>
                      ) : null}
                      {children}
                    </Menu.Item>
                  );
                } else if (
                  isSlot(action, Tooltip) &&
                  isSlot(action.props.children, ActionButton)
                ) {
                  return (
                    <Menu.Item
                      onSelect={
                        (action.props.children.props.onClick
                          ? action.props.children.props.onClick
                          : () => undefined) as () => void
                      } // onClick of ActionButton type expects a MouseEvent at the moment, should maybe be widened
                      {...action.props.children}
                    >
                      {action.props.children.props.prefixIcon ? (
                        <Menu.Prefix>
                          {action.props.children.props.prefixIcon}
                        </Menu.Prefix>
                      ) : null}
                      {action.props.children.props.children}
                      <Menu.ItemTooltip>{action.props.text}</Menu.ItemTooltip>
                    </Menu.Item>
                  );
                }

                return null;
              })}
            </Menu.Group>
            {Object.entries(slots.actionGroups).map(([groupId, actions]) => (
              <Menu.Group key={groupId}>
                {actions.map((action) => {
                  if (isSlot(action, ActionButton)) {
                    const {
                      prefixIcon,
                      children,
                      showLabel,
                      onClick,
                      ...rest
                    } = action.props;

                    return (
                      <Menu.Item
                        {...rest}
                        key={action.key}
                        onSelect={
                          (onClick ? onClick : () => undefined) as () => void
                        } // onClick of ActionButton type expects a MouseEvent at the moment, should maybe be widened
                      >
                        {prefixIcon ? (
                          <Menu.Prefix>{prefixIcon}</Menu.Prefix>
                        ) : null}
                        {children}
                      </Menu.Item>
                    );
                  } else if (
                    isSlot(action, Tooltip) &&
                    isSlot(action.props.children, ActionButton)
                  ) {
                    return (
                      <Menu.Item
                        onSelect={
                          (action.props.children.props.onClick
                            ? action.props.children.props.onClick
                            : () => undefined) as () => void
                        } // onClick of ActionButton type expects a MouseEvent at the moment, should maybe be widened
                        {...action.props.children}
                      >
                        {action.props.children.props.prefixIcon ? (
                          <Menu.Prefix>
                            {action.props.children.props.prefixIcon}
                          </Menu.Prefix>
                        ) : null}
                        {action.props.children.props.children}
                        <Menu.ItemTooltip>{action.props.text}</Menu.ItemTooltip>
                      </Menu.Item>
                    );
                  }

                  return null;
                })}
              </Menu.Group>
            ))}
          </Menu.Content>
        </Menu>
      ) : null}
    </>
  );
});

(ActionItems as typeof ActionItems & { displayName: string }).displayName =
  'AppHeader.ActionItems';
